// The kinds of middlegame position (`MiddlegamePosition`), read from a FEN
// alone and from one side's point of view (the reader's): the queens, the
// centre's pawns, where the kings stand, and the piece imbalances the
// books name. A position can be of several kinds at once, and of none.
// Pure: no engine, no chess library. The middlegame-positions fact
// (`facts/middlegame-positions.ts`) classifies each of the reader's
// middlegame moves with it; the card's help topic says the same rules in words.
import type { Color, Fen, MiddlegamePosition } from '@chess/api-model';

/** A side's king is exposed with fewer than this many of its own pawns on the three files around it, one and two ranks in front. */
export const KING_SHIELD_PAWNS = 2;
/** The centre is open with at most this many pawns, of both sides, on the d- and e-files (and none blocked). */
export const OPEN_CENTRE_PAWNS = 2;

const FILES = 'abcdefgh';

/** The board as a FEN draws it: `at(file, rank)` with file 0 (a) to 7 (h) and rank 1 to 8; the piece letter (White upper case), or undefined. */
class Board {
  private readonly squares = new Map<string, string>();
  constructor(fen: Fen) {
    const ranks = fen.split(' ')[0].split('/');
    ranks.forEach((row, i) => {
      let file = 0;
      for (const ch of row) {
        if (/\d/.test(ch)) file += Number(ch);
        else {
          this.squares.set(`${file}${8 - i}`, ch);
          file += 1;
        }
      }
    });
  }
  at(file: number, rank: number): string | undefined {
    return this.squares.get(`${file}${rank}`);
  }
  /** Every piece with its square. */
  *pieces(): Generator<{ piece: string; file: number; rank: number }> {
    for (const [sq, piece] of this.squares) yield { piece, file: Number(sq[0]), rank: Number(sq[1]) };
  }
}

const isWhite = (piece: string) => piece === piece.toUpperCase();
const sideOf = (piece: string): Color => (isWhite(piece) ? 'white' : 'black');
const own = (side: Color, type: string) => (side === 'white' ? type.toUpperCase() : type.toLowerCase());

interface Counts {
  queens: number;
  rooks: number;
  bishops: number;
  knights: number;
  king?: { file: number; rank: number };
}

function countsOf(board: Board): Record<Color, Counts> {
  const counts: Record<Color, Counts> = {
    white: { queens: 0, rooks: 0, bishops: 0, knights: 0 },
    black: { queens: 0, rooks: 0, bishops: 0, knights: 0 },
  };
  for (const { piece, file, rank } of board.pieces()) {
    const c = counts[sideOf(piece)];
    switch (piece.toLowerCase()) {
      case 'q':
        c.queens += 1;
        break;
      case 'r':
        c.rooks += 1;
        break;
      case 'b':
        c.bishops += 1;
        break;
      case 'n':
        c.knights += 1;
        break;
      case 'k':
        c.king = { file, rank };
        break;
    }
  }
  return counts;
}

/** Whether `side`'s king stands off its first two ranks, or with fewer than `KING_SHIELD_PAWNS` of its pawns on the files around it one and two ranks ahead. */
export function kingExposed(fen: Fen, side: Color): boolean {
  const board = new Board(fen);
  const king = countsOf(board)[side].king;
  if (!king) return false;
  const forward = side === 'white' ? 1 : -1;
  const relativeRank = side === 'white' ? king.rank : 9 - king.rank;
  if (relativeRank > 2) return true;
  const pawn = own(side, 'p');
  let shield = 0;
  for (let file = king.file - 1; file <= king.file + 1; file++) {
    if (file < 0 || file > 7) continue;
    for (const step of [1, 2]) if (board.at(file, king.rank + step * forward) === pawn) shield += 1;
  }
  return shield < KING_SHIELD_PAWNS;
}

/** The centre by its pawns: `closed` when a pawn on the d- or e-file has an enemy pawn right in front of it, `open` when at most `OPEN_CENTRE_PAWNS` pawns stand on those files, else neither. */
export function centreOf(fen: Fen): 'open' | 'closed' | undefined {
  const board = new Board(fen);
  let pawns = 0;
  for (const file of [FILES.indexOf('d'), FILES.indexOf('e')]) {
    for (let rank = 1; rank <= 8; rank++) {
      const p = board.at(file, rank);
      if (p === 'P') {
        pawns += 1;
        if (board.at(file, rank + 1) === 'p') return 'closed';
      } else if (p === 'p') pawns += 1;
    }
  }
  return pawns <= OPEN_CENTRE_PAWNS ? 'open' : undefined;
}

/**
 * The kinds of the position for `side` (the reader), in `MIDDLEGAME_POSITIONS`' order:
 * `queens-on` both sides have a queen, `queens-off` neither has;
 * `open-centre` / `closed-centre` (`centreOf`);
 * `opposite-castling` one king on the a–c files and the other on the g–h files;
 * `exposed-king` the side's king exposed (`kingExposed`) while the other side has a queen;
 * `bishop-pair` the side has two bishops or more and the other fewer, `their-bishop-pair` the reverse;
 * `exchange-up` a rook more and a minor piece (bishop or knight) fewer than the other side, `exchange-down` the reverse.
 */
export function positionTypesOf(fen: Fen, side: Color): MiddlegamePosition[] {
  const board = new Board(fen);
  const counts = countsOf(board);
  const me = counts[side];
  const them = counts[side === 'white' ? 'black' : 'white'];
  const out: MiddlegamePosition[] = [];
  if (me.queens > 0 && them.queens > 0) out.push('queens-on');
  if (me.queens === 0 && them.queens === 0) out.push('queens-off');
  const centre = centreOf(fen);
  if (centre === 'open') out.push('open-centre');
  if (centre === 'closed') out.push('closed-centre');
  const wing = (k?: { file: number }) => (k === undefined ? undefined : k.file <= 2 ? 'queen' : k.file >= 6 ? 'king' : undefined);
  const mine = wing(me.king);
  const theirs = wing(them.king);
  if (mine && theirs && mine !== theirs) out.push('opposite-castling');
  if (them.queens > 0 && kingExposed(fen, side)) out.push('exposed-king');
  if (me.bishops >= 2 && them.bishops < 2) out.push('bishop-pair');
  if (them.bishops >= 2 && me.bishops < 2) out.push('their-bishop-pair');
  const rooks = me.rooks - them.rooks;
  const minors = me.bishops + me.knights - (them.bishops + them.knights);
  if (rooks === 1 && minors === -1) out.push('exchange-up');
  if (rooks === -1 && minors === 1) out.push('exchange-down');
  return out;
}
